import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';

// ── Entity type config — mirrors the OBJECTS pattern in App.js ────────────────
const ENTITY_TYPES = [
    {
        key: 'Artist',
        endpoint: '/artists',
        extractData: d => Array.isArray(d) ? d : [],
        getLabel: r => r.name,
        getSublabel: r => [r.city, r.state].filter(Boolean).join(', '),
    },
    {
        key: 'Festival',
        endpoint: '/festivals',
        extractData: d => d.festivals || [],
        getLabel: r => r.name,
        getSublabel: () => '',
    },
    {
        key: 'Promoter',
        endpoint: '/promoters',
        extractData: d => d.promoters || [],
        getLabel: r => r.name,
        getSublabel: r => [r.city, r.state].filter(Boolean).join(', '),
    },
    {
        key: 'Venue',
        endpoint: '/venues',
        extractData: d => Array.isArray(d) ? d : [],
        getLabel: r => r.name,
        getSublabel: r => [r.city, r.state].filter(Boolean).join(', '),
    },
];

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'
    });
}

const styles = {
    container: { maxWidth: '1100px', margin: '0 auto', padding: '32px 24px', color: '#e0e0e0' },
    heading: { fontSize: '26px', fontWeight: 700, color: '#fff', marginBottom: '6px' },
    subheading: { fontSize: '14px', color: '#888', marginBottom: '28px' },
    card: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '20px', marginBottom: '20px' },
    label: { fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' },
    // Top row
    topRow: { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' },
    topField: { display: 'flex', flexDirection: 'column' },
    memberField: { minWidth: '180px' },
    entityTypeField: { minWidth: '130px' },
    entitySearchField: { flex: 1, minWidth: '200px', position: 'relative' },
    roleField: { minWidth: '200px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', padding: '9px 12px', fontSize: '14px', outline: 'none', height: '38px', boxSizing: 'border-box' },
    inputReadOnly: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '6px', color: '#888', padding: '9px 12px', fontSize: '14px', height: '38px', boxSizing: 'border-box' },
    select: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', padding: '9px 12px', fontSize: '14px', height: '38px', boxSizing: 'border-box', cursor: 'pointer', outline: 'none' },
    searchBtn: { backgroundColor: '#bb86fc', border: 'none', borderRadius: '6px', color: '#121212', padding: '9px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', height: '38px', whiteSpace: 'nowrap', alignSelf: 'flex-end' },
    searchBtnDisabled: { backgroundColor: '#555', color: '#999', cursor: 'default' },
    // Entity dropdown
    entityDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '260px', overflowY: 'auto', zIndex: 300, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' },
    entityDropdownItem: { padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid #333', fontSize: '14px' },
    // Results
    resultsHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' },
    resultsTitle: { fontSize: '15px', fontWeight: 600, color: '#e0e0e0' },
    selectAllRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', backgroundColor: '#222', borderRadius: '6px 6px 0 0', borderBottom: '1px solid #2a2a2a', fontSize: '13px', color: '#aaa' },
    eventRow: { display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: '12px', alignItems: 'center', padding: '11px 14px', borderBottom: '1px solid #1e1e1e', fontSize: '13px' },
    checkbox: { width: '16px', height: '16px', cursor: 'pointer', accentColor: '#bb86fc' },
    eventDate: { color: '#aaa', fontSize: '12px', whiteSpace: 'nowrap', marginTop: '2px' },
    venueName: { color: '#bb86fc', textDecoration: 'none', fontWeight: 500 },
    locationStr: { color: '#666', fontSize: '12px' },
    festivalBadge: { display: 'inline-block', backgroundColor: '#1a2a0a', color: '#8bc34a', border: '1px solid #4a6a1a', borderRadius: '4px', padding: '1px 7px', fontSize: '11px', marginLeft: '6px' },
    lineupStr: { color: '#555', fontSize: '11px', marginTop: '2px' },
    emptyState: { padding: '40px', textAlign: 'center', color: '#555' },
    // Bottom action row
    actionRow: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap' },
    addRolesBtn: { backgroundColor: '#1a3a1a', border: '1px solid #2d6a2d', borderRadius: '6px', color: '#4caf50', padding: '10px 24px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    addRolesBtnDisabled: { backgroundColor: '#1a1a1a', border: '1px solid #333', color: '#555', cursor: 'default' },
    clearBtn: { backgroundColor: 'transparent', border: '1px solid #444', borderRadius: '6px', color: '#aaa', padding: '10px 16px', fontSize: '14px', cursor: 'pointer' },
    selectedCount: { fontSize: '13px', color: '#888' },
    successMsg: { color: '#4caf50', fontSize: '14px' },
    errorMsg: { color: '#cf6679', fontSize: '14px' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px', marginBottom: '20px' },
};

export default function ProductionRoleMapping() {
    const { memberId } = useParams();
    const navigate = useNavigate();

    // Member info
    const [member, setMember] = useState(null);
    const [memberLoading, setMemberLoading] = useState(true);

    // Entity type + search
    const [entityType, setEntityType] = useState('Artist');
    const [entityQuery, setEntityQuery] = useState('');
    const [entityRecords, setEntityRecords] = useState([]);
    const [entityLoading, setEntityLoading] = useState(false);
    const [entityLoadedType, setEntityLoadedType] = useState(null);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
    const entityContainerRef = useRef(null);

    // Role
    const [role, setRole] = useState('');

    // Events results
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // Selection
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Submission
    const [submitting, setSubmitting] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    // ── Load member name ──────────────────────────────────────────────────────
    useEffect(() => {
        axios.get(`${config.API_URL}/members/${memberId}`)
            .then(res => setMember(res.data.member))
            .catch(() => setMember({ id: memberId, name: `Member #${memberId}` }))
            .finally(() => setMemberLoading(false));
    }, [memberId]);

    // ── Load entity list when type changes ────────────────────────────────────
    const loadEntityRecords = useCallback(async (type) => {
        if (entityLoadedType === type) return;
        const cfg = ENTITY_TYPES.find(e => e.key === type);
        if (!cfg) return;
        setEntityLoading(true);
        try {
            const res = await axios.get(`${config.API_URL}${cfg.endpoint}`);
            setEntityRecords(cfg.extractData(res.data));
            setEntityLoadedType(type);
        } catch {
            setEntityRecords([]);
        }
        setEntityLoading(false);
    }, [entityLoadedType]);

    const handleEntityTypChange = (e) => {
        const type = e.target.value;
        setEntityType(type);
        setEntityQuery('');
        setSelectedEntity(null);
        setEntityDropdownOpen(false);
        setEntityLoadedType(null);
        setEntityRecords([]);
        setEvents([]);
        setHasSearched(false);
        setSelectedIds(new Set());
    };

    const handleEntityFocus = () => {
        loadEntityRecords(entityType);
        setEntityDropdownOpen(true);
    };

    const handleEntityInput = (e) => {
        setEntityQuery(e.target.value);
        setSelectedEntity(null);
        setEntityDropdownOpen(true);
        loadEntityRecords(entityType);
    };

    // Close entity dropdown on outside click
    useEffect(() => {
        function onOut(e) {
            if (entityContainerRef.current && !entityContainerRef.current.contains(e.target)) {
                setEntityDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    const entityCfg = ENTITY_TYPES.find(e => e.key === entityType);
    const filteredEntities = entityQuery.trim()
        ? entityRecords.filter(r =>
            entityCfg.getLabel(r).toLowerCase().includes(entityQuery.toLowerCase()) ||
            entityCfg.getSublabel(r).toLowerCase().includes(entityQuery.toLowerCase())
          )
        : entityRecords.slice(0, 40); // cap unfiltered list for performance

    const handleEntitySelect = (record) => {
        setSelectedEntity(record);
        setEntityQuery(entityCfg.getLabel(record));
        setEntityDropdownOpen(false);
        setEvents([]);
        setHasSearched(false);
        setSelectedIds(new Set());
    };

    // ── Search events ─────────────────────────────────────────────────────────
    const handleSearch = async () => {
        if (!selectedEntity || !role.trim()) return;
        setEventsLoading(true);
        setHasSearched(true);
        setSelectedIds(new Set());
        setSuccessMsg('');
        setErrorMsg('');
        try {
            const res = await axios.get(`${config.API_URL}/members/${memberId}/production-events`, {
                params: { entity_type: entityType, entity_id: selectedEntity.id }
            });
            setEvents(res.data.events || []);
        } catch (err) {
            setErrorMsg('Failed to load events. Please try again.');
            setEvents([]);
        }
        setEventsLoading(false);
    };

    // ── Selection ─────────────────────────────────────────────────────────────
    const allSelected = events.length > 0 && selectedIds.size === events.length;

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(events.map(e => e.event_id)));
        }
    };

    const toggleEvent = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // ── Clear ─────────────────────────────────────────────────────────────────
    const handleClear = () => {
        setSelectedIds(new Set());
        setRole('');
        setEntityQuery('');
        setSelectedEntity(null);
        setEvents([]);
        setHasSearched(false);
        setSuccessMsg('');
        setErrorMsg('');
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleAddRoles = async () => {
        if (!selectedEntity || !role.trim() || selectedIds.size === 0) return;
        setSubmitting(true);
        setSuccessMsg('');
        setErrorMsg('');
        try {
            const token = await getIdToken();
            const res = await axios.post(
                `${config.API_URL}/members/${memberId}/production-roles`,
                {
                    entity_type: entityType,
                    entity_id: selectedEntity.id,
                    role: role.trim(),
                    event_ids: [...selectedIds],
                },
                { headers: { Authorization: token } }
            );
            const { assigned, skipped } = res.data;
            const msg = `${assigned} role${assigned !== 1 ? 's' : ''} assigned${skipped > 0 ? `, ${skipped} skipped (already existed)` : ''}.`;
            // Redirect to member detail after short delay so user sees confirmation
            setSuccessMsg(msg + ' Redirecting...');
            setTimeout(() => navigate(`/members/${memberId}`), 1500);
        } catch (err) {
            setErrorMsg('Failed to assign roles. Please try again.');
        }
        setSubmitting(false);
    };

    const canSearch = selectedEntity && role.trim();
    const canSubmit = canSearch && selectedIds.size > 0 && !submitting;

    if (memberLoading) return <div style={{ color: '#e0e0e0', padding: '40px', textAlign: 'center' }}>Loading...</div>;

    return (
        <div style={styles.container}>
            <Link to={`/members/${memberId}`} style={styles.backLink}>← Back to {member?.name || 'Member'}</Link>

            <div style={styles.heading}>Add Production Role</div>
            <div style={styles.subheading}>
                Assign a production role to one or more events for <strong style={{ color: '#e0e0e0' }}>{member?.name}</strong>.
            </div>

            {/* ── Top input row ─────────────────────────────────────────── */}
            <div style={styles.card}>
                <div style={styles.topRow}>

                    {/* Member ID — read only */}
                    <div style={{ ...styles.topField, ...styles.memberField }}>
                        <div style={styles.label}>Member</div>
                        <div style={styles.inputReadOnly}>{member?.name || memberId}</div>
                    </div>

                    {/* Entity type dropdown */}
                    <div style={{ ...styles.topField, ...styles.entityTypeField }}>
                        <div style={styles.label}>Entity Type</div>
                        <select
                            value={entityType}
                            onChange={handleEntityTypChange}
                            style={styles.select}
                        >
                            {ENTITY_TYPES.map(et => (
                                <option key={et.key} value={et.key}>{et.key}</option>
                            ))}
                        </select>
                    </div>

                    {/* Entity search */}
                    <div style={{ ...styles.topField, ...styles.entitySearchField }} ref={entityContainerRef}>
                        <div style={styles.label}>Search {entityType}</div>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                value={entityQuery}
                                onChange={handleEntityInput}
                                onFocus={handleEntityFocus}
                                placeholder={`Type to search ${entityType.toLowerCase()}s...`}
                                style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
                                autoComplete="off"
                            />
                            {entityDropdownOpen && (entityLoading || filteredEntities.length > 0 || entityQuery) && (
                                <div style={styles.entityDropdown}>
                                    {entityLoading && (
                                        <div style={{ padding: '10px 14px', fontSize: '13px', color: '#777' }}>
                                            Loading {entityType.toLowerCase()}s...
                                        </div>
                                    )}
                                    {!entityLoading && filteredEntities.length === 0 && (
                                        <div style={{ padding: '10px 14px', fontSize: '13px', color: '#777' }}>
                                            No results for "{entityQuery}"
                                        </div>
                                    )}
                                    {!entityLoading && filteredEntities.map(r => (
                                        <div
                                            key={r.id}
                                            onMouseDown={() => handleEntitySelect(r)}
                                            style={styles.entityDropdownItem}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <div style={{ color: '#e0e0e0' }}>{entityCfg.getLabel(r)}</div>
                                            {entityCfg.getSublabel(r) && (
                                                <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>{entityCfg.getSublabel(r)}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Production role */}
                    <div style={{ ...styles.topField, ...styles.roleField }}>
                        <div style={styles.label}>Production Role</div>
                        <input
                            type="text"
                            value={role}
                            onChange={e => setRole(e.target.value)}
                            placeholder="e.g. Merch Manager, Sound Engineer..."
                            style={styles.input}
                        />
                    </div>

                    {/* Search button */}
                    <button
                        onClick={handleSearch}
                        disabled={!canSearch}
                        style={{ ...styles.searchBtn, ...(!canSearch ? styles.searchBtnDisabled : {}) }}
                    >
                        Search
                    </button>
                </div>

                {!selectedEntity && (
                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#555' }}>
                        Select an entity and enter a production role, then click Search to find eligible events.
                    </div>
                )}
                {selectedEntity && !role.trim() && (
                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#777' }}>
                        Enter a production role to enable Search.
                    </div>
                )}
            </div>

            {/* ── Results ──────────────────────────────────────────────────── */}
            {(hasSearched || eventsLoading) && (
                <div style={styles.card}>
                    <div style={styles.resultsHeader}>
                        <div style={styles.resultsTitle}>
                            {eventsLoading
                                ? 'Loading events...'
                                : events.length === 0
                                    ? 'No eligible events found'
                                    : `${events.length} eligible event${events.length !== 1 ? 's' : ''} · ${selectedIds.size} selected`
                            }
                        </div>
                    </div>

                    {eventsLoading ? (
                        <div style={styles.emptyState}>Loading...</div>
                    ) : events.length === 0 ? (
                        <div style={styles.emptyState}>
                            No eligible events found. {member?.name} may already be assigned to all events for this entity, or no events exist.
                        </div>
                    ) : (
                        <>
                            {/* Select all */}
                            <div style={styles.selectAllRow}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={toggleSelectAll}
                                    style={styles.checkbox}
                                    id="select-all"
                                />
                                <label htmlFor="select-all" style={{ cursor: 'pointer' }}>
                                    Select All ({events.length} events)
                                </label>
                            </div>

                            {/* Event rows */}
                            <div style={{ backgroundColor: '#141414', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
                                {events.map((ev, idx) => (
                                    <div
                                        key={ev.event_id}
                                        style={{
                                            ...styles.eventRow,
                                            backgroundColor: selectedIds.has(ev.event_id) ? '#1a2a1a' : idx % 2 === 0 ? '#141414' : '#181818',
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(ev.event_id)}
                                            onChange={() => toggleEvent(ev.event_id)}
                                            style={styles.checkbox}
                                        />
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                <Link to={`/events/${ev.event_slug}`} style={styles.venueName}>{ev.venue_name}</Link>
                                                {ev.festival_name && (
                                                    <span style={styles.festivalBadge}>{ev.festival_name}</span>
                                                )}
                                            </div>
                                            <div style={styles.eventDate}>{formatDate(ev.event_date)}</div>
                                            <div style={styles.locationStr}>
                                                {[ev.city, ev.state, ev.country].filter(Boolean).join(', ')}
                                            </div>
                                            {ev.lineup && (
                                                <div style={styles.lineupStr}>{ev.lineup}</div>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#555', textAlign: 'right' }}>
                                            #{ev.event_id}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Action row */}
                            <div style={styles.actionRow}>
                                <button
                                    onClick={handleAddRoles}
                                    disabled={!canSubmit}
                                    style={{ ...styles.addRolesBtn, ...(!canSubmit ? styles.addRolesBtnDisabled : {}) }}
                                >
                                    {submitting ? 'Assigning...' : `Add Role to ${selectedIds.size} Event${selectedIds.size !== 1 ? 's' : ''}`}
                                </button>
                                <button onClick={handleClear} style={styles.clearBtn}>Clear</button>
                                {selectedIds.size > 0 && (
                                    <span style={styles.selectedCount}>{selectedIds.size} of {events.length} selected</span>
                                )}
                                {successMsg && <span style={styles.successMsg}>{successMsg}</span>}
                                {errorMsg && <span style={styles.errorMsg}>{errorMsg}</span>}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
